import { MenuItem, Typography } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import {
  useMutation,
  useNotify,
  useListContext,
  sanitizeFetchType,
  GET_MANY,
  GetManyResult,
  useTranslate,
} from 'react-admin'
import { useHasAuthority } from '../hooks/useHasAuthority'
import { Authority } from '../../core/auth/Authority'
import { ResourceName } from '../../core/ResourceName'
import { ApiErrorDto } from '../../core/dto/error-handling/api-error.dto'
import {
  ProgressStatus,
  StateBulkProgressDialog,
} from './StateBulkProgressDialog'
import { BulkActionProps } from './BulkActionsProps'
import { ConfirmDialog } from './ConfirmDialog'
import { VerifierDeviceBaseDto } from '../../core/dto/device/common/verifier-device-base/verifier-device-base.dto'

export const DeviceResetModulesBulkButton = ({
  selectedIds,
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  innerRef,
  ...props
}: BulkActionProps) => {
  const { loading, refetch } = useListContext<VerifierDeviceBaseDto>()
  const { resource } = props as { resource: ResourceName }
  const notify = useNotify()
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const [resetModulesDialogOpen, setResetModulesDialogOpen] = useState(false)
  const [mutate, { loading: mutationLoading }] = useMutation()
  const [bulkStateDialogOpen, setBulkStateDialogOpen] = useState(false)
  const [bulkProgressStatus, setBulkProgressStatus] = useState<
    ProgressStatus[]
  >([])
  const updateBulkProgressStatus = (newStatus: ProgressStatus) => {
    const oldStatusIndex = bulkProgressStatus.findIndex(
      (s) => s.id === newStatus.id,
    )
    bulkProgressStatus.splice(oldStatusIndex, 1, newStatus)
  }
  const [selectedDevices, setSelectedDevices] = useState<
    VerifierDeviceBaseDto[]
  >([])

  const handleMutation = async (device: VerifierDeviceBaseDto) => {
    let error
    let result = null
    try {
      result = await mutate(
        {
          type: sanitizeFetchType('resetModules'),
          resource,
          payload: {
            id: device.id,
          },
        },
        {
          returnPromise: true,
        },
      )
    } catch (err) {
      error = err
    }
    if (error) {
      updateBulkProgressStatus({
        id: device.id,
        status: 'error',
        error: error as ApiErrorDto,
      } as ProgressStatus)
      return error
    }
    updateBulkProgressStatus({
      id: device.id,
      status: 'success',
    } as ProgressStatus)
    return result
  }

  const handleResetModules = async () => {
    setBulkStateDialogOpen(true)
    setResetModulesDialogOpen(false)
    const pendingRequests: Promise<any>[] = []
    let selectedDevicesResponse
    try {
      selectedDevicesResponse = (await mutate(
        {
          type: sanitizeFetchType(GET_MANY),
          resource,
          payload: {
            ids: selectedIds,
          },
        },
        { returnPromise: true, onFailure: (msg) => notify(msg, 'error') },
      )) as GetManyResult<VerifierDeviceBaseDto>
    } catch (err) {
      setBulkStateDialogOpen(false)
      notify((err as Error)?.message, 'error')
      return
    }
    if (!selectedDevicesResponse.data.length) {
      setBulkStateDialogOpen(false)
      return
    }
    selectedDevicesResponse.data.forEach((device: VerifierDeviceBaseDto) => {
      pendingRequests.push(handleMutation(device))
      selectedDevices.push(device)
      bulkProgressStatus.push({
        id: device.id,
        status: 'pending',
      } as ProgressStatus)
    })
    await Promise.all(pendingRequests)
    refetch()
  }

  useEffect(() => {
    if (!bulkStateDialogOpen) {
      setBulkProgressStatus([])
      setSelectedDevices([])
    }
  }, [bulkStateDialogOpen])

  return (
    <MenuItem
      {...props}
      disabled={
        !selectedIds.length ||
        (resource === ResourceName.BARRIERS &&
          !hasAuthority(Authority.BARRIERS_RESET_MODULES_BUTTON)) ||
        (resource === ResourceName.DOORS &&
          !hasAuthority(Authority.DOORS_RESET_MODULES)) ||
        (resource === ResourceName.ENGINES &&
          !hasAuthority(Authority.ENGINES_RESET_MODULES_BUTTON)) ||
        (resource === ResourceName.PASSAGES &&
          !hasAuthority(Authority.PASSAGES_RESET_MODULES_BUTTON)) ||
        (resource === ResourceName.TERMINALS &&
          !hasAuthority(Authority.TERMINALS_RESET_MODULES)) ||
        loading ||
        mutationLoading
      }
      onClick={() => setResetModulesDialogOpen(true)}
      button
    >
      <Typography>{translate('resources.modules.resetModules')}</Typography>
      <ConfirmDialog
        open={resetModulesDialogOpen}
        confirm={handleResetModules}
        source="resetModules"
        cancel={() => setResetModulesDialogOpen(false)}
        action="set"
        resource={resource}
      />
      <StateBulkProgressDialog
        open={bulkStateDialogOpen}
        close={() => setBulkStateDialogOpen(false)}
        elements={selectedDevices}
        method="resetModules"
        statuses={bulkProgressStatus}
        resource={resource}
      />
    </MenuItem>
  )
}
